import * as React from "react";
import { cn } from "../shared/ui/index.ts";
import { pg } from "../shared/page-layout.ts";
import {
  appointmentView,
  type AgendaAct,
  type AgendaAppointment,
  type AgendaAppointmentView,
  type AgendaChair,
} from "./appointments.ts";
import { formatHour, isSameDay, todayLocal, toISODate } from "./date-utils.ts";

interface SchedulerSettings {
  startHour: number;
  endHour: number;
  pauseEnabled: boolean;
  breakStart: number;
  breakEnd: number;
  cellMinutes: number;
}

const ACT_DROP_PREVIEW_ID = "__agenda-act-drop__";

type PositionedAppointment = AgendaAppointmentView & {
  lane: number;
  laneCount: number;
};

export function DayScheduler({
  selectedDate,
  appointments,
  acts,
  chairs,
  settings,
  draggingActId,
  onEventClick,
  onEventDoubleClick,
  onEventChangeRequest,
  onCreateAppointment,
  onActDrop,
}: {
  selectedDate: Date;
  appointments: AgendaAppointment[];
  acts: AgendaAct[];
  chairs: AgendaChair[];
  settings: SchedulerSettings;
  draggingActId?: string | null;
  onEventClick?: (e: AgendaAppointment) => void;
  onEventDoubleClick?: (e: AgendaAppointment) => void;
  onEventChangeRequest?: (
    appointment: AgendaAppointment,
    change: Partial<Pick<AgendaAppointment, "start" | "dur" | "chairId">>,
  ) => void;
  onCreateAppointment?: (slot: {
    date: string;
    start: number;
    dur: number;
    chairId: string;
  }) => void;
  onActDrop?: (drop: {
    actId: string;
    date: string;
    start: number;
    chairId: string;
    dur: number;
  }) => void;
}) {
  const clickTimer = React.useRef<number | null>(null);
  const suppressClick = React.useRef(false);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const bodyHeight = useElementHeight(bodyRef);
  const [dragPreview, setDragPreview] = React.useState<{
    id: string;
    dx: number;
    dy: number;
  } | null>(null);
  const [resizePreview, setResizePreview] = React.useState<{
    id: string;
    dur: number;
  } | null>(null);
  const [dropPreview, setDropPreview] = React.useState<{
    id: string;
    chairId: string;
    start: number;
    dur: number;
  } | null>(null);
  const [drawPreview, setDrawPreview] = React.useState<{
    chairId: string;
    start: number;
    dur: number;
  } | null>(null);
  const today = todayLocal();
  const isToday = isSameDay(selectedDate, today);
  const startHour = Math.min(settings.startHour, settings.endHour - 1);
  const endHour = Math.max(settings.endHour, startHour + 1);
  const hours = Array.from(
    { length: endHour - startHour },
    (_, i) => startHour + i,
  );
  const events = appointments
    .filter((appointment) => appointment.date === toISODate(selectedDate))
    .map((appointment) => appointmentView(appointment, acts));
  const breakStart = Math.max(
    startHour,
    Math.min(settings.breakStart, endHour),
  );
  const breakEnd = Math.max(breakStart, Math.min(settings.breakEnd, endHour));
  const pauseActive = settings.pauseEnabled && breakEnd > breakStart;
  const visibleHours =
    endHour - startHour - (pauseActive ? breakEnd - breakStart : 0);
  const rowHeight = hourHeight(settings.cellMinutes, visibleHours, bodyHeight);
  const minuteStep = settings.cellMinutes / 60;
  const actDurationById = new Map(
    acts.map((act) => [act.id, act.duration / 60]),
  );

  React.useEffect(() => {
    if (!draggingActId) {
      setDropPreview((current) =>
        current?.id === ACT_DROP_PREVIEW_ID ? null : current,
      );
    }
  }, [draggingActId]);

  React.useEffect(() => {
    return () => {
      if (clickTimer.current !== null) window.clearTimeout(clickTimer.current);
    };
  }, []);

  const scheduleSingleClick = (event: AgendaAppointment) => {
    if (clickTimer.current !== null) window.clearTimeout(clickTimer.current);
    clickTimer.current = window.setTimeout(() => {
      onEventClick?.(event);
      clickTimer.current = null;
    }, 220);
  };

  const runDoubleClick = (event: AgendaAppointment) => {
    if (clickTimer.current !== null) {
      window.clearTimeout(clickTimer.current);
      clickTimer.current = null;
    }
    onEventDoubleClick?.(event);
  };

  const startResize = (
    event: React.PointerEvent,
    appointment: AgendaAppointmentView,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    const originY = event.clientY;
    const originDur = appointment.dur;
    let nextDur = originDur;
    suppressClick.current = true;
    document.body.style.userSelect = "none";
    document.body.style.cursor = "ns-resize";

    const onMove = (moveEvent: PointerEvent) => {
      moveEvent.preventDefault();
      const deltaHours = (moveEvent.clientY - originY) / rowHeight;
      nextDur = clamp(
        snapToStep(originDur + deltaHours, minuteStep),
        minuteStep,
        endHour - appointment.start,
      );
      setResizePreview({ id: appointment.id, dur: nextDur });
      setDropPreview({
        id: appointment.id,
        chairId: appointment.chairId,
        start: appointment.start,
        dur: nextDur,
      });
    };
    const onUp = () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      setResizePreview(null);
      setDropPreview(null);
      if (nextDur !== originDur) {
        onEventChangeRequest?.(appointment, {
          start: appointment.start,
          dur: nextDur,
        });
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const startMove = (
    event: React.PointerEvent,
    appointment: AgendaAppointmentView,
    chairIndex: number,
  ) => {
    event.stopPropagation();
    const originX = event.clientX;
    const originY = event.clientY;
    event.currentTarget.setPointerCapture(event.pointerId);
    const originStart = appointment.start;
    const originVisualTop =
      visibleTop(originStart, startHour, breakStart, breakEnd, pauseActive) *
      rowHeight;
    const originChairId = appointment.chairId;
    const columnWidth =
      (event.currentTarget.parentElement?.getBoundingClientRect().width ?? 1) ||
      1;
    let nextStart = originStart;
    let nextChairId = originChairId;
    let moved = false;
    let active = false;
    const activateDrag = () => {
      active = true;
      suppressClick.current = true;
      setDragPreview({ id: appointment.id, dx: 0, dy: 0 });
      setDropPreview({
        id: appointment.id,
        chairId: originChairId,
        start: originStart,
        dur: appointment.dur,
      });
      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";
    };
    const holdTimer = window.setTimeout(activateDrag, 180);

    const onMove = (moveEvent: PointerEvent) => {
      if (!active) return;
      moveEvent.preventDefault();
      const deltaY = moveEvent.clientY - originY;
      const deltaX = moveEvent.clientX - originX;
      if (Math.abs(deltaY) > 3 || Math.abs(deltaX) > 3) {
        moved = true;
        suppressClick.current = true;
      }
      setDragPreview({ id: appointment.id, dx: deltaX, dy: deltaY });
      nextStart = clamp(
        snapToStep(
          visibleOffsetToHour(
            originVisualTop + deltaY,
            rowHeight,
            startHour,
            breakStart,
            breakEnd,
            pauseActive,
          ),
          minuteStep,
        ),
        startHour,
        endHour - appointment.dur,
      );
      const nextChairIndex = clamp(
        chairIndex + Math.round(deltaX / columnWidth),
        0,
        chairs.length - 1,
      );
      nextChairId = chairs[nextChairIndex]?.id ?? originChairId;
      setDropPreview({
        id: appointment.id,
        chairId: nextChairId,
        start: nextStart,
        dur: appointment.dur,
      });
    };
    const onUp = () => {
      window.clearTimeout(holdTimer);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      document.body.style.userSelect = "";
      document.body.style.cursor = "";
      setDragPreview(null);
      setDropPreview(null);
      if (active && moved) {
        onEventChangeRequest?.(appointment, {
          start: nextStart,
          chairId: nextChairId,
        });
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  const startCreateDraw = (
    event: React.PointerEvent<HTMLDivElement>,
    chair: AgendaChair,
  ) => {
    if (event.button !== 0 && event.pointerType === "mouse") return;
    if (draggingActId) return;
    const target = event.target as Element;
    if (target.closest("[data-agenda-event]")) return;

    event.preventDefault();

    const columnEl = event.currentTarget;
    const rect = columnEl.getBoundingClientRect();
    const initialOffsetY = event.clientY - rect.top;
    const initialHour = visibleOffsetToHour(
      initialOffsetY,
      rowHeight,
      startHour,
      breakStart,
      breakEnd,
      pauseActive,
    );
    const initialStart = clamp(
      snapToStep(initialHour, minuteStep),
      startHour,
      endHour - minuteStep,
    );
    let nextStart = initialStart;
    let nextDur = minuteStep;
    let moved = false;
    try {
      columnEl.setPointerCapture(event.pointerId);
    } catch {
      /* ignore */
    }
    suppressClick.current = true;
    document.body.style.userSelect = "none";
    setDrawPreview({ chairId: chair.id, start: nextStart, dur: nextDur });

    const updateFromY = (clientY: number) => {
      const offsetY = clientY - rect.top;
      const currentHour = visibleOffsetToHour(
        offsetY,
        rowHeight,
        startHour,
        breakStart,
        breakEnd,
        pauseActive,
      );
      let snappedStart: number;
      let snappedEnd: number;
      if (currentHour >= initialHour) {
        snappedStart = initialStart;
        snappedEnd = clamp(
          snapToStep(currentHour, minuteStep),
          snappedStart + minuteStep,
          endHour,
        );
      } else {
        snappedEnd = initialStart + minuteStep;
        snappedStart = clamp(
          snapToStep(currentHour, minuteStep),
          startHour,
          snappedEnd - minuteStep,
        );
      }
      nextStart = snappedStart;
      nextDur = snappedEnd - snappedStart;
      setDrawPreview({ chairId: chair.id, start: nextStart, dur: nextDur });
    };

    const onMove = (moveEvent: PointerEvent) => {
      if (moveEvent.pointerId !== event.pointerId) return;
      moveEvent.preventDefault();
      if (Math.abs(moveEvent.clientY - event.clientY) > 3) moved = true;
      updateFromY(moveEvent.clientY);
    };
    const onUp = (upEvent: PointerEvent) => {
      if (upEvent.pointerId !== event.pointerId) return;
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      document.body.style.userSelect = "";
      setDrawPreview(null);
      try {
        columnEl.releasePointerCapture(event.pointerId);
      } catch {
        /* ignore */
      }
      if (!moved) {
        const fallbackDur = (acts[0]?.duration ?? 30) / 60;
        onCreateAppointment?.({
          date: toISODate(selectedDate),
          start: initialStart,
          dur: Math.max(minuteStep, fallbackDur),
          chairId: chair.id,
        });
        return;
      }
      onCreateAppointment?.({
        date: toISODate(selectedDate),
        start: nextStart,
        dur: nextDur,
        chairId: chair.id,
      });
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  return (
    <div className={pg.scheduler}>
      <div className={cn(pg.schedulerHeader, "flex-col")}>
        <div className="flex border-b border-sf-border">
          <div className="w-14 shrink-0" />
          <div className="flex-1 px-2 py-2 text-center">
            <div
              className={cn(
                "text-lg font-bold capitalize leading-tight",
                isToday ? "text-sf-primary" : "text-sf-text",
              )}
            >
              {shortDayLabel(selectedDate)}
            </div>
            <div className="mt-0.5 text-xs font-semibold text-sf-primary">
              {events.length} rendez-vous
            </div>
          </div>
        </div>
        <div className="flex">
          <div className="w-14 shrink-0" />
          {chairs.map((chair) => (
            <div
              key={chair.id}
              className="min-w-0 flex-1 border-l border-sf-border px-2 py-2 text-center"
            >
              <div className="truncate text-xs font-bold uppercase tracking-wide text-sf-text-secondary">
                {chair.label}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div ref={bodyRef} className={pg.schedulerBody}>
        <div className="relative flex h-full">
          {pauseActive && (
            <PauseSeparator
              top={
                visibleTop(breakStart, startHour, breakStart, breakEnd) *
                rowHeight
              }
              label={`Pause ${formatHour(breakStart)} - ${formatHour(breakEnd)}`}
            />
          )}
          <TimeRuler
            startHour={startHour}
            endHour={endHour}
            breakStart={breakStart}
            breakEnd={breakEnd}
            pauseActive={pauseActive}
            rowHeight={rowHeight}
          />
          {chairs.map((chair, chairIndex) => {
            const chairEvents = layoutAppointments(
              events.filter((event) => event.chairId === chair.id),
            );
            return (
              <div
                key={chair.id}
                role="presentation"
                onDragOver={(ev) => {
                  if (
                    !ev.dataTransfer.types.includes("application/x-agenda-act")
                  ) {
                    return;
                  }
                  ev.preventDefault();
                  if (!draggingActId) return;
                  const rect = ev.currentTarget.getBoundingClientRect();
                  const dur = actDurationById.get(draggingActId) ?? minuteStep;
                  const start = clamp(
                    snapToStep(
                      visibleOffsetToHour(
                        ev.clientY - rect.top,
                        rowHeight,
                        startHour,
                        breakStart,
                        breakEnd,
                        pauseActive,
                      ),
                      minuteStep,
                    ),
                    startHour,
                    endHour - dur,
                  );
                  setDropPreview({
                    id: ACT_DROP_PREVIEW_ID,
                    chairId: chair.id,
                    start,
                    dur,
                  });
                }}
                onDragLeave={(ev) => {
                  const next = ev.relatedTarget as Node | null;
                  if (next && ev.currentTarget.contains(next)) return;
                  setDropPreview((current) =>
                    current?.id === ACT_DROP_PREVIEW_ID ? null : current,
                  );
                }}
                onDrop={(ev) => {
                  const actId = ev.dataTransfer.getData(
                    "application/x-agenda-act",
                  );
                  setDropPreview((current) =>
                    current?.id === ACT_DROP_PREVIEW_ID ? null : current,
                  );
                  if (!actId) return;
                  ev.preventDefault();
                  const rect = ev.currentTarget.getBoundingClientRect();
                  const dur = actDurationById.get(actId) ?? minuteStep;
                  const start = clamp(
                    snapToStep(
                      visibleOffsetToHour(
                        ev.clientY - rect.top,
                        rowHeight,
                        startHour,
                        breakStart,
                        breakEnd,
                        pauseActive,
                      ),
                      minuteStep,
                    ),
                    startHour,
                    endHour - dur,
                  );
                  onActDrop?.({
                    actId,
                    date: toISODate(selectedDate),
                    start,
                    chairId: chair.id,
                    dur,
                  });
                }}
                onPointerDown={(ev) => startCreateDraw(ev, chair)}
                className="relative flex min-w-0 flex-1 flex-col border-l border-sf-border touch-none"
              >
                {hours.map((h, i) =>
                  pauseActive && h >= breakStart && h < breakEnd ? null : (
                    <HourCell
                      key={h}
                      shaded={i % 2 === 1}
                      cellMinutes={settings.cellMinutes}
                    />
                  ),
                )}
                {chairEvents.map((e) => (
                  <div
                    key={e.id}
                    data-agenda-event
                    role="presentation"
                    onPointerDown={(ev) => startMove(ev, e, chairIndex)}
                    onDragStart={(ev) => ev.preventDefault()}
                    onSelect={(ev) => ev.preventDefault()}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      if (suppressClick.current) {
                        suppressClick.current = false;
                        return;
                      }
                      scheduleSingleClick(e);
                    }}
                    onDoubleClick={(ev) => {
                      ev.stopPropagation();
                      runDoubleClick(e);
                    }}
                    className={cn(
                      "absolute cursor-grab overflow-hidden rounded-md px-1.5 py-1 select-none touch-none active:cursor-grabbing",
                      dragPreview?.id === e.id &&
                        "z-20 opacity-80 shadow-lg ring-2 ring-sf-primary/30",
                      appointmentStatus(e, isToday) === "done"
                        ? "bg-gray-50 opacity-60"
                        : "opacity-100",
                    )}
                    style={{
                      left: `calc(${(e.lane / e.laneCount) * 100}% + 4px)`,
                      width: `calc(${100 / e.laneCount}% - 8px)`,
                      top:
                        visibleTop(
                          e.start,
                          startHour,
                          breakStart,
                          breakEnd,
                          pauseActive,
                        ) *
                          rowHeight +
                        2,
                      height:
                        (resizePreview?.id === e.id
                          ? resizePreview.dur
                          : e.dur) *
                          rowHeight -
                        4,
                      transform:
                        dragPreview?.id === e.id
                          ? `translate(${dragPreview.dx}px, ${dragPreview.dy}px)`
                          : undefined,
                      ...(appointmentStatus(e, isToday) !== "done"
                        ? { backgroundColor: `${e.color}14` }
                        : {}),
                      borderLeftWidth: 4,
                      borderLeftStyle: "solid",
                      borderLeftColor: e.color,
                    }}
                  >
                    <div className="truncate text-[12.5px] font-semibold text-sf-text">
                      {e.patient}
                    </div>
                    <div className="truncate text-[11px] text-sf-text-muted">
                      {e.motif} · {e.practitioner.initials}
                    </div>
                    <div className="truncate text-[10.5px] font-medium text-sf-text-muted">
                      {formatHour(e.start)} – {formatHour(e.start + e.dur)}
                    </div>
                    <button
                      type="button"
                      aria-label="Redimensionner le rendez-vous"
                      onPointerDown={(ev) => {
                        // Sur tactile : pas de resize, seulement le move.
                        if (ev.pointerType === "touch") return;
                        startResize(ev, e);
                      }}
                      className="absolute inset-x-1 bottom-0 h-3 cursor-ns-resize touch-none rounded-b bg-transparent pointer-coarse:hidden"
                    />
                  </div>
                ))}
                {dropPreview && dropPreview.chairId === chair.id && (
                  <DropIndicator
                    top={
                      visibleTop(
                        dropPreview.start,
                        startHour,
                        breakStart,
                        breakEnd,
                        pauseActive,
                      ) * rowHeight
                    }
                    height={dropPreview.dur * rowHeight}
                    label={`${formatHour(dropPreview.start)} – ${formatHour(
                      dropPreview.start + dropPreview.dur,
                    )}`}
                  />
                )}
                {drawPreview && drawPreview.chairId === chair.id && (
                  <DrawIndicator
                    top={
                      visibleTop(
                        drawPreview.start,
                        startHour,
                        breakStart,
                        breakEnd,
                        pauseActive,
                      ) * rowHeight
                    }
                    height={drawPreview.dur * rowHeight}
                    label={`${formatHour(drawPreview.start)} – ${formatHour(
                      drawPreview.start + drawPreview.dur,
                    )}`}
                  />
                )}
                {isToday && isCurrentTimeVisible(startHour, endHour) && (
                  <div
                    className="absolute left-0 right-0 bg-sf-danger"
                    style={{
                      top:
                        visibleTop(
                          currentHour(),
                          startHour,
                          breakStart,
                          breakEnd,
                          pauseActive,
                        ) * rowHeight,
                      height: 2,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function appointmentStatus(
  event: { start: number; dur: number },
  isToday: boolean,
): "done" | "current" | "upcoming" {
  if (!isToday) return "upcoming";
  const hour = currentHour();
  if (event.start + event.dur <= hour) return "done";
  if (event.start <= hour) return "current";
  return "upcoming";
}

function currentHour(): number {
  const now = new Date();
  return now.getHours() + now.getMinutes() / 60;
}

function isCurrentTimeVisible(startHour: number, endHour: number): boolean {
  const hour = currentHour();
  return hour >= startHour && hour <= endHour;
}

function layoutAppointments(
  events: AgendaAppointmentView[],
): PositionedAppointment[] {
  const sorted = [...events].sort((a, b) => a.start - b.start);
  const positioned: PositionedAppointment[] = [];
  let group: PositionedAppointment[] = [];
  let groupEnd = -Infinity;

  const closeGroup = () => {
    const laneCount = Math.max(1, ...group.map((event) => event.lane + 1));
    for (const event of group) event.laneCount = laneCount;
    group = [];
    groupEnd = -Infinity;
  };

  for (const event of sorted) {
    if (group.length > 0 && event.start >= groupEnd) closeGroup();
    const used = new Set(
      group
        .filter((candidate) => candidate.start + candidate.dur > event.start)
        .map((candidate) => candidate.lane),
    );
    let lane = 0;
    while (used.has(lane)) lane += 1;
    const positionedEvent = { ...event, lane, laneCount: 1 };
    group.push(positionedEvent);
    positioned.push(positionedEvent);
    groupEnd = Math.max(groupEnd, event.start + event.dur);
  }
  if (group.length > 0) closeGroup();

  return positioned;
}

function hourHeight(
  _cellMinutes: number,
  visibleHours: number,
  availableHeight: number,
): number {
  return Math.max(1, availableHeight) / Math.max(1, visibleHours);
}

function visibleTop(
  hour: number,
  startHour: number,
  breakStart: number,
  breakEnd: number,
  pauseActive = true,
): number {
  if (!pauseActive || hour <= breakStart) return hour - startHour;
  if (hour >= breakEnd) return hour - startHour - (breakEnd - breakStart);
  return breakStart - startHour;
}

function visibleOffsetToHour(
  offsetPx: number,
  rowHeight: number,
  startHour: number,
  breakStart: number,
  breakEnd: number,
  pauseActive: boolean,
): number {
  const visibleHour = offsetPx / rowHeight;
  const breakVisibleStart = breakStart - startHour;
  if (!pauseActive || visibleHour <= breakVisibleStart) {
    return startHour + visibleHour;
  }
  return startHour + visibleHour + (breakEnd - breakStart);
}

function snapToStep(value: number, step: number): number {
  return Math.round(value / step) * step;
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function shortDayLabel(date: Date): string {
  const day = date
    .toLocaleDateString("fr-FR", { weekday: "short" })
    .replace(".", "");
  return `${capitalize(day)} ${date.getDate()}`;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function HourCell({
  shaded,
  cellMinutes,
}: {
  shaded: boolean;
  cellMinutes: number;
}) {
  const cells = Math.max(1, Math.round(60 / cellMinutes));
  return (
    <div
      className={cn(
        "flex flex-1 flex-col",
        shaded ? "bg-black/[0.015]" : "bg-white",
      )}
    >
      {Array.from({ length: cells }, (_, k) => (
        <div key={k} className="flex-1 border-b border-sf-border/40" />
      ))}
    </div>
  );
}

function TimeRuler({
  startHour,
  endHour,
  breakStart,
  breakEnd,
  pauseActive,
  rowHeight,
}: {
  startHour: number;
  endHour: number;
  breakStart: number;
  breakEnd: number;
  pauseActive: boolean;
  rowHeight: number;
}) {
  interface Label {
    hour: number;
    align: "top" | "center" | "bottom";
    anchor: "top" | "bottom";
    top: number;
  }
  const labels: Label[] = [];
  for (let hour = startHour; hour <= endHour; hour += 1) {
    if (pauseActive && hour > breakStart && hour < breakEnd) continue;
    const top =
      visibleTop(hour, startHour, breakStart, breakEnd, pauseActive) *
      rowHeight;
    let align: Label["align"] = "center";
    if (hour === startHour) align = "top";
    else if (hour === endHour) align = "bottom";
    else if (pauseActive && hour === breakStart) align = "bottom";
    else if (pauseActive && hour === breakEnd) align = "top";
    const anchor: Label["anchor"] = hour === endHour ? "bottom" : "top";
    labels.push({ hour, top, align, anchor });
  }

  return (
    <div className="relative h-full w-14 shrink-0">
      {labels.map(({ hour, top, align, anchor }) => (
        <div
          key={hour}
          className="absolute right-0 pr-2 text-[11px] font-medium tabular-nums leading-none text-sf-text-muted"
          style={{
            ...(anchor === "bottom" ? { bottom: 0 } : { top }),
            transform:
              align === "top"
                ? "translateY(0)"
                : align === "bottom"
                  ? "translateY(0)"
                  : "translateY(-50%)",
          }}
        >
          {String(hour).padStart(2, "0")}:00
        </div>
      ))}
    </div>
  );
}

function PauseSeparator({ top, label }: { top: number; label: string }) {
  return (
    <div
      className="pointer-events-none absolute left-0 right-0 z-[6]"
      style={{ top }}
    >
      <div className="absolute bottom-[3px] left-2 bg-white/90 px-1 text-[9px] font-semibold uppercase tracking-[0.16em] text-sf-primary/75">
        {label}
      </div>
      <div className="h-0 border-t border-dashed border-sf-primary/70" />
    </div>
  );
}

function DropIndicator({
  top,
  height,
  label,
}: {
  top: number;
  height: number;
  label: string;
}) {
  return (
    <div
      className="pointer-events-none absolute left-1 right-1 z-[15] rounded-md border-2 border-dashed border-sf-primary bg-sf-primary-50/70"
      style={{ top, height: Math.max(18, height) }}
    >
      <div className="absolute -top-5 left-0 rounded bg-sf-primary px-1.5 py-0.5 text-[10px] font-semibold text-white shadow-sm">
        {label}
      </div>
    </div>
  );
}

function DrawIndicator({
  top,
  height,
  label,
}: {
  top: number;
  height: number;
  label: string;
}) {
  return (
    <div
      className="pointer-events-none absolute left-1 right-1 z-[16] flex flex-col overflow-hidden rounded-md border-2 border-sf-primary bg-sf-primary-50 shadow-[0_4px_12px_rgba(79,70,229,.18)]"
      style={{ top, height: Math.max(20, height) }}
    >
      <div className="bg-sf-primary px-1.5 py-0.5 text-[10px] font-semibold text-white">
        {label}
      </div>
      <div className="flex flex-1 items-center justify-center px-2 text-[11px] font-medium text-sf-primary">
        Nouveau rendez-vous
      </div>
    </div>
  );
}

function useElementHeight(ref: React.RefObject<HTMLElement | null>): number {
  const [height, setHeight] = React.useState(0);

  React.useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    const update = () => setHeight(node.getBoundingClientRect().height);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [ref]);

  return height;
}
